import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Clock, Heart, Sparkles, Crown, Shield, FileText, Globe, HelpCircle, Star, UserPlus, LogIn, Download, X, Smartphone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MemioLogo from "@/components/MemioLogo";
import Footer from "@/components/Footer";
import { initiateGoogleAuth } from "@/utils/pwaAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  
  // PWA Install states
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  
  // Consent states
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [ageConsent, setAgeConsent] = useState(false);
  const [dataConsent, setDataConsent] = useState(false);

  // PWA Install prompt handler
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if user dismissed the banner before
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed) {
      const dismissedTime = new Date(dismissed).getTime();
      const now = new Date().getTime();
      // Show again after 7 days
      if (now - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      // Show custom install banner
      setShowInstallBanner(true);
      console.log('PWA install prompt captured');
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallBanner(false);
      setDeferredPrompt(null);
      console.log('PWA was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      console.log('No deferred prompt available');
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa_install_dismissed', new Date().toISOString());
  };

  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        if (response.data) {
          navigate("/dashboard");
        }
      } catch {
        // Not authenticated, stay on landing
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkExistingAuth();
  }, [navigate]);

  const handleLoginClick = () => {
    // Check if user has already consented (stored in localStorage)
    const hasConsented = localStorage.getItem('user_consent');
    if (hasConsented) {
      proceedWithLogin();
    } else {
      setShowConsentDialog(true);
    }
  };

  const handleConsentAndLogin = () => {
    if (!privacyConsent || !termsConsent || !ageConsent || !dataConsent) {
      toast.error(t('legal.consent.requiredFields'));
      return;
    }

    // Store consent
    localStorage.setItem('user_consent', JSON.stringify({
      privacy: true,
      terms: true,
      age: true,
      data: true,
      timestamp: new Date().toISOString(),
      language: i18n.language
    }));

    setShowConsentDialog(false);
    proceedWithLogin();
  };

  const proceedWithLogin = () => {
    initiateGoogleAuth('/dashboard');
  };

  if (isCheckingAuth) {
    return (
      <div className="loading-container" data-testid="landing-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="landing-hero hero-gradient" data-testid="landing-page">
      {/* PWA Install Banner */}
      {showInstallBanner && !isInstalled && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-[#D4A373] to-[#8D6E63] text-white shadow-lg animate-slide-up">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Smartphone className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-sm sm:text-base">{t('pwa.installTitle') || 'Install Memio App'}</p>
                <p className="text-xs sm:text-sm text-white/80">{t('pwa.installDescription') || 'Add to home screen for quick access'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleInstallClick}
                className="bg-white text-[#8D6E63] hover:bg-white/90 text-sm px-4 py-2"
                size="sm"
              >
                <Download className="w-4 h-4 mr-1" />
                {t('pwa.install') || 'Install'}
              </Button>
              <button
                onClick={dismissInstallBanner}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top Bar with Language Selector and Auth Buttons */}
      <div className="absolute top-0 left-0 right-0 z-20 px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-2 text-sm text-[#8D6E63]">
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{t('common.language')}:</span>
          <LanguageSwitcher />
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/login">
            <Button 
              variant="ghost" 
              size="sm"
              className="text-[#8D6E63] hover:text-[#6D4C41] hover:bg-[#D4A373]/10 text-sm font-medium px-2 sm:px-3"
              data-testid="header-login-btn"
            >
              <LogIn className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('landing.logIn')}</span>
            </Button>
          </Link>
          <Link to="/signup">
            <Button 
              size="sm"
              className="bg-[#8D6E63] hover:bg-[#6D4C41] text-white text-sm font-medium px-2 sm:px-3"
              data-testid="header-signup-btn"
            >
              <UserPlus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('landing.signUp')}</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* Decorative photo stacks - Hidden on mobile */}
      <div className="photo-stack photo-stack-1 hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1583567775437-82d66b14fa61?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxub3N0YWxnaWMlMjBmYW1pbHklMjBtb21lbnRzJTIwOTBzJTIwZmlsbSUyMHN0eWxlfGVufDB8fHx8MTc2NjUxODUyMnww&ixlib=rb-4.1.0&q=85&w=300"
          alt="Nostalgic moment"
          className="w-full h-[160px] object-cover"
          loading="lazy"
          decoding="async"
        />
        <p className="font-handwritten text-center mt-2 text-[#8D6E63]">Summer '18</p>
      </div>

      <div className="photo-stack photo-stack-2 hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1680817707021-778b24728368?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxub3N0YWxnaWMlMjBmYW1pbHklMjBtb21lbnRzJTIwOTBzJTIwZmlsbSUyMHN0eWxlfGVufDB8fHx8MTc2NjUxODUyMnww&ixlib=rb-4.1.0&q=85&w=300"
          alt="Memory"
          className="w-full h-[160px] object-cover"
          loading="lazy"
          decoding="async"
        />
        <p className="font-handwritten text-center mt-2 text-[#8D6E63]">Best friends</p>
      </div>

      {/* Main Content */}
      <div className="landing-content">
        <div className="max-w-2xl mx-auto animate-fade-in-up">
          {/* Logo/Brand */}
          <div className="mb-8 flex justify-center">
            <MemioLogo size="xl" />
          </div>

          {/* Headline */}
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#4A4A4A] mb-6 leading-tight">
            {t('landing.tagline')}
            <br />
            <span className="text-[#8D6E63]">{t('landing.taglineHighlight')}</span>
          </h1>

          {/* Subheadline */}
          <p className="text-base sm:text-lg text-[#8D6E63] mb-8 max-w-lg mx-auto">
            {t('landing.subtitle')}
          </p>

          {/* Feature highlights */}
          <div className="flex flex-wrap justify-center gap-6 mb-10">
            <div className="flex items-center gap-2 text-[#4A4A4A]">
              <Clock className="w-5 h-5 text-[#D4A373]" />
              <span className="text-sm">{t('landing.features.onThisDay')}</span>
            </div>
            <div className="flex items-center gap-2 text-[#4A4A4A]">
              <Heart className="w-5 h-5 text-[#E57373]" />
              <span className="text-sm">{t('landing.features.nostalgicFeed')}</span>
            </div>
            <div className="flex items-center gap-2 text-[#4A4A4A]">
              <Sparkles className="w-5 h-5 text-[#D4A373]" />
              <span className="text-sm">{t('landing.features.sentimentInsights')}</span>
            </div>
          </div>

          {/* CTA Buttons - Sign Up & Log In */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/signup">
              <Button
                className="btn-vintage text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-shadow"
                size="lg"
                data-testid="signup-button"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                {t('landing.signUpFree') || 'Sign Up Free'}
              </Button>
            </Link>
            
            <Link to="/login">
              <Button
                variant="outline"
                className="text-lg border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373] hover:text-white"
                size="lg"
                data-testid="email-login-button"
              >
                <LogIn className="w-5 h-5 mr-2" />
                {t('landing.logIn') || 'Log In'}
              </Button>
            </Link>
          </div>

          {/* Install App Button - Only shown when install prompt is available */}
          {deferredPrompt && !isInstalled && (
            <div className="mt-4">
              <Button
                onClick={handleInstallClick}
                variant="outline"
                size="lg"
                className="border-[#81C784] text-[#81C784] hover:bg-[#81C784] hover:text-white"
              >
                <Download className="w-5 h-5 mr-2" />
                {t('pwa.installTitle') || 'Install Memio App'}
              </Button>
            </div>
          )}

          {/* Free signup pitch */}
          <div className="mt-5 inline-flex items-center gap-2 bg-gradient-to-r from-[#D4A373]/10 to-[#8D6E63]/10 px-5 py-2.5 rounded-full border border-[#D4A373]/30">
            <span className="text-lg">✨</span>
            <p className="text-base font-semibold bg-gradient-to-r from-[#D4A373] to-[#8D6E63] bg-clip-text text-transparent">
              {t('landing.freeSignupPitch') || 'Free forever. No credit card required.'}
            </p>
          </div>

          {/* Google Sign In & Pricing */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mt-4">
            <Button
              onClick={handleLoginClick}
              variant="outline"
              size="lg"
              className="border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373] hover:text-white"
              data-testid="login-button"
            >
              <Mail className="w-5 h-5 mr-2" />
              {t('landing.signInWithGoogle')}
            </Button>
            
            <Link to="/pricing">
              <Button
                variant="outline"
                size="lg"
                className="border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373] hover:text-white"
                data-testid="pricing-button"
              >
                <Crown className="w-5 h-5 mr-2" />
                {t('landing.viewPricing')}
              </Button>
            </Link>
          </div>

          <p className="text-sm text-[#A1887F] mt-6">
            {t('landing.privacyNote')}
          </p>

          {/* Legal Links */}
          <div className="flex flex-wrap justify-center items-center gap-4 mt-4 text-sm">
            <Link 
              to="/privacy" 
              className="text-[#8D6E63] hover:text-[#D4A373] flex items-center gap-1"
              data-testid="privacy-link"
            >
              <Shield className="w-3 h-3" />
              {t('legal.privacyPolicy')}
            </Link>
            <span className="text-[#BCAAA4]">|</span>
            <Link 
              to="/terms" 
              className="text-[#8D6E63] hover:text-[#D4A373] flex items-center gap-1"
              data-testid="terms-link"
            >
              <FileText className="w-3 h-3" />
              {t('legal.termsOfService')}
            </Link>
            <span className="text-[#BCAAA4]">|</span>
            <Link 
              to="/faq" 
              className="text-[#8D6E63] hover:text-[#D4A373] flex items-center gap-1"
              data-testid="faq-link"
            >
              <HelpCircle className="w-3 h-3" />
              FAQ
            </Link>
          </div>
        </div>
      </div>

      {/* Unlock All Memories Section */}
      <div className="relative z-10 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="memory-card p-8">
            <Crown className="w-12 h-12 text-[#D4A373] mx-auto mb-4" />
            <h2 className="font-serif text-2xl sm:text-3xl text-[#4A4A4A] mb-3">
              {t('dashboard.unlockAll') || 'Unlock All Memories'}
            </h2>
            <p className="text-[#8D6E63] mb-6 max-w-md mx-auto">
              {t('dashboard.upgradePrompt') || 'Upgrade to Premium to unlock all your memories from every year!'}
            </p>
            <Link to="/pricing">
              <Button className="btn-vintage">
                <Sparkles className="w-4 h-4 mr-2" />
                {t('landing.viewPricing') || 'View Pricing'}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Sample Emails Section */}
      <div className="relative z-10 py-12 px-4 bg-[#F5F1E8]/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-handwritten text-2xl text-[#8D6E63] text-center mb-2">
            {t('landing.sampleEmails.title') || 'Sample Emails'}
          </h2>
          <p className="text-sm text-[#A1887F] text-center mb-8">
            {t('landing.sampleEmails.subtitle') || 'See what your memories could look like'}
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Sample Email 1 - 1 Year Ago */}
            <div className="memory-card p-4 transform hover:scale-[1.02] transition-transform">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full font-medium">
                  {t('landing.sampleEmails.sample')} • 1 {t('landing.sampleEmails.yearAgo')}
                </span>
                <span className="text-lg">🌟</span>
              </div>
              <h3 className="font-serif text-sm text-[#4A4A4A] mb-1 truncate">
                {t('landing.sampleEmails.email1.subject')}
              </h3>
              <p className="text-xs text-[#8D6E63] mb-2">To: {t('landing.sampleEmails.email1.to')}</p>
              <p className="text-xs text-[#A1887F] line-clamp-2">
                "{t('landing.sampleEmails.email1.snippet')}"
              </p>
              <div className="mt-3 pt-2 border-t border-[#EFEBE9] flex justify-between items-center">
                <span className="text-xs text-[#A1887F]">Dec 26, 2024</span>
                <span className="text-xs text-[#8D6E63]">⭐ {t('landing.sampleEmails.email1.sentiment')}</span>
              </div>
            </div>

            {/* Sample Email 2 - 3 Years Ago */}
            <div className="memory-card p-4 transform hover:scale-[1.02] transition-transform">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full font-medium">
                  {t('landing.sampleEmails.sample')} • 3 {t('landing.sampleEmails.yearsAgo')}
                </span>
                <span className="text-lg">🎉</span>
              </div>
              <h3 className="font-serif text-sm text-[#4A4A4A] mb-1 truncate">
                {t('landing.sampleEmails.email2.subject')}
              </h3>
              <p className="text-xs text-[#8D6E63] mb-2">To: {t('landing.sampleEmails.email2.to')}</p>
              <p className="text-xs text-[#A1887F] line-clamp-2">
                "{t('landing.sampleEmails.email2.snippet')}"
              </p>
              <div className="mt-3 pt-2 border-t border-[#EFEBE9] flex justify-between items-center">
                <span className="text-xs text-[#A1887F]">Dec 26, 2022</span>
                <span className="text-xs text-[#8D6E63]">🎊 {t('landing.sampleEmails.email2.sentiment')}</span>
              </div>
            </div>

            {/* Sample Email 3 - 5 Years Ago */}
            <div className="memory-card p-4 transform hover:scale-[1.02] transition-transform">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full font-medium">
                  {t('landing.sampleEmails.sample')} • 5 {t('landing.sampleEmails.yearsAgo')}
                </span>
                <span className="text-lg">✈️</span>
              </div>
              <h3 className="font-serif text-sm text-[#4A4A4A] mb-1 truncate">
                {t('landing.sampleEmails.email3.subject')}
              </h3>
              <p className="text-xs text-[#8D6E63] mb-2">To: {t('landing.sampleEmails.email3.to')}</p>
              <p className="text-xs text-[#A1887F] line-clamp-2">
                "{t('landing.sampleEmails.email3.snippet')}"
              </p>
              <div className="mt-3 pt-2 border-t border-[#EFEBE9] flex justify-between items-center">
                <span className="text-xs text-[#A1887F]">Dec 26, 2020</span>
                <span className="text-xs text-[#8D6E63]">✨ {t('landing.sampleEmails.email3.sentiment')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Install App Section - Always visible on mobile */}
      <div className="relative z-10 py-12 px-4 bg-gradient-to-r from-[#D4A373]/10 to-[#8D6E63]/10" id="install-app">
        <div className="max-w-3xl mx-auto text-center">
          <div className="memory-card p-8">
            <Smartphone className="w-12 h-12 text-[#D4A373] mx-auto mb-4" />
            <h2 className="font-serif text-2xl sm:text-3xl text-[#4A4A4A] mb-3">
              {t('pwa.installSection.title') || 'Install Memio on Your Phone'}
            </h2>
            <p className="text-[#8D6E63] mb-6 max-w-md mx-auto">
              {t('pwa.installSection.description') || 'Get quick access to your memories anytime. Install Memio as an app on your device.'}
            </p>
            
            {/* Show native install button if available */}
            {deferredPrompt && !isInstalled ? (
              <Button
                onClick={handleInstallClick}
                className="btn-vintage mb-6"
                size="lg"
              >
                <Download className="w-5 h-5 mr-2" />
                {t('pwa.install') || 'Install Now'}
              </Button>
            ) : isInstalled ? (
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full mb-6">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('pwa.alreadyInstalled') || 'App Installed!'}
              </div>
            ) : null}

            {/* Installation Instructions */}
            <div className="grid md:grid-cols-2 gap-6 text-left">
              {/* Android Instructions */}
              <div className="bg-white/80 rounded-xl p-5 border border-[#EFEBE9]">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-6 h-6 text-[#3DDC84]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.523 2.234a.5.5 0 00-.866 0l-1.064 1.843a6.01 6.01 0 00-2.093-.377c-.735 0-1.44.132-2.093.377L10.343 2.234a.5.5 0 00-.866 0L8.413 4.077A6.017 6.017 0 006.5 9.5h11a6.017 6.017 0 00-1.913-5.423l1.936-1.843zM9 8a1 1 0 110-2 1 1 0 010 2zm6 0a1 1 0 110-2 1 1 0 010 2zm-8 3v7.5A1.5 1.5 0 008.5 20h1v2.5a1.5 1.5 0 103 0V20h1.5v2.5a1.5 1.5 0 103 0V20h.5a1.5 1.5 0 001.5-1.5V11H7zm-2.5 0A1.5 1.5 0 003 12.5v4a1.5 1.5 0 103 0v-4A1.5 1.5 0 004.5 11zm15 0a1.5 1.5 0 00-1.5 1.5v4a1.5 1.5 0 103 0v-4a1.5 1.5 0 00-1.5-1.5z"/>
                  </svg>
                  <h3 className="font-semibold text-[#4A4A4A]">{t('pwa.android.title') || 'Android'}</h3>
                </div>
                <ol className="text-sm text-[#8D6E63] space-y-2">
                  <li className="flex gap-2">
                    <span className="font-bold text-[#D4A373]">1.</span>
                    {t('pwa.android.step1') || 'Tap the menu icon (⋮) in Chrome'}
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold text-[#D4A373]">2.</span>
                    {t('pwa.android.step2') || 'Select "Add to Home screen"'}
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold text-[#D4A373]">3.</span>
                    {t('pwa.android.step3') || 'Tap "Add" to install'}
                  </li>
                </ol>
              </div>

              {/* iOS Instructions */}
              <div className="bg-white/80 rounded-xl p-5 border border-[#EFEBE9]">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-6 h-6 text-[#555]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <h3 className="font-semibold text-[#4A4A4A]">{t('pwa.ios.title') || 'iPhone / iPad'}</h3>
                </div>
                <ol className="text-sm text-[#8D6E63] space-y-2">
                  <li className="flex gap-2">
                    <span className="font-bold text-[#D4A373]">1.</span>
                    {t('pwa.ios.step1') || 'Tap the Share button in Safari'}
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold text-[#D4A373]">2.</span>
                    {t('pwa.ios.step2') || 'Scroll down and tap "Add to Home Screen"'}
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold text-[#D4A373]">3.</span>
                    {t('pwa.ios.step3') || 'Tap "Add" to install'}
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="relative z-10 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-handwritten text-2xl text-[#8D6E63] text-center mb-8">
            {t('landing.testimonials.title')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-white/80 rounded-xl p-6 border border-[#EFEBE9] shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#D4A373] text-[#D4A373]" />
                ))}
              </div>
              <p className="text-[#4A4A4A] text-sm mb-4 italic">
                "{t('landing.testimonials.t1.quote')}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4A373] flex items-center justify-center text-white font-medium">
                  {t('landing.testimonials.t1.name').charAt(0)}
                </div>
                <div>
                  <p className="font-medium text-[#4A4A4A] text-sm">{t('landing.testimonials.t1.name')}</p>
                  <p className="text-xs text-[#A1887F]">{t('landing.testimonials.t1.role')}</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white/80 rounded-xl p-6 border border-[#EFEBE9] shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#D4A373] text-[#D4A373]" />
                ))}
              </div>
              <p className="text-[#4A4A4A] text-sm mb-4 italic">
                "{t('landing.testimonials.t2.quote')}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#8D6E63] flex items-center justify-center text-white font-medium">
                  {t('landing.testimonials.t2.name').charAt(0)}
                </div>
                <div>
                  <p className="font-medium text-[#4A4A4A] text-sm">{t('landing.testimonials.t2.name')}</p>
                  <p className="text-xs text-[#A1887F]">{t('landing.testimonials.t2.role')}</p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white/80 rounded-xl p-6 border border-[#EFEBE9] shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#D4A373] text-[#D4A373]" />
                ))}
              </div>
              <p className="text-[#4A4A4A] text-sm mb-4 italic">
                "{t('landing.testimonials.t3.quote')}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#81C784] flex items-center justify-center text-white font-medium">
                  {t('landing.testimonials.t3.name').charAt(0)}
                </div>
                <div>
                  <p className="font-medium text-[#4A4A4A] text-sm">{t('landing.testimonials.t3.name')}</p>
                  <p className="text-xs text-[#A1887F]">{t('landing.testimonials.t3.role')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom decorative elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#F5F1E8] to-transparent pointer-events-none" />
      
      {/* Footer */}
      <footer className="relative z-10 pb-6 text-center">
        <p className="font-handwritten text-lg text-[#8D6E63]">
          {t('landing.quote')}
        </p>
        <p className="text-xs text-[#A1887F] mt-2">{t('landing.quoteAuthor')}</p>
      </footer>

      {/* Consent Dialog */}
      <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
        <DialogContent className="bg-[#FDFBF7] border-2 border-[#EFEBE9] max-w-md" data-testid="consent-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#4A4A4A] flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#8D6E63]" />
              {t('legal.consent.title')}
            </DialogTitle>
            <DialogDescription className="text-[#8D6E63]">
              {t('legal.consent.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Privacy Policy Consent */}
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#EFEBE9]">
              <Checkbox
                id="privacy"
                checked={privacyConsent}
                onCheckedChange={setPrivacyConsent}
                className="mt-0.5"
                data-testid="privacy-checkbox"
              />
              <label htmlFor="privacy" className="text-sm text-[#4A4A4A] cursor-pointer">
                {t('legal.consent.privacyText')}{' '}
                <Link to="/privacy" target="_blank" className="text-[#D4A373] hover:underline">
                  {t('legal.privacyPolicy')}
                </Link>
              </label>
            </div>

            {/* Terms of Service Consent */}
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#EFEBE9]">
              <Checkbox
                id="terms"
                checked={termsConsent}
                onCheckedChange={setTermsConsent}
                className="mt-0.5"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms" className="text-sm text-[#4A4A4A] cursor-pointer">
                {t('legal.consent.termsText')}{' '}
                <Link to="/terms" target="_blank" className="text-[#D4A373] hover:underline">
                  {t('legal.termsOfService')}
                </Link>
              </label>
            </div>

            {/* Age Consent */}
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#EFEBE9]">
              <Checkbox
                id="age"
                checked={ageConsent}
                onCheckedChange={setAgeConsent}
                className="mt-0.5"
                data-testid="age-checkbox"
              />
              <label htmlFor="age" className="text-sm text-[#4A4A4A] cursor-pointer">
                {t('legal.consent.ageConfirm')}
              </label>
            </div>

            {/* Data Access Consent */}
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#EFEBE9]">
              <Checkbox
                id="data"
                checked={dataConsent}
                onCheckedChange={setDataConsent}
                className="mt-0.5"
                data-testid="data-checkbox"
              />
              <label htmlFor="data" className="text-sm text-[#4A4A4A] cursor-pointer">
                {t('legal.consent.dataConsent')}
              </label>
            </div>

            {/* Continue Button */}
            <Button
              onClick={handleConsentAndLogin}
              className="w-full btn-vintage"
              disabled={!privacyConsent || !termsConsent || !ageConsent || !dataConsent}
              data-testid="consent-continue-button"
            >
              <Mail className="w-4 h-4 mr-2" />
              {t('legal.consent.continueButton')}
            </Button>

            <p className="text-xs text-center text-[#A1887F]">
              {t('landing.privacyNote')}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
